<template>
  <signup-promo v-if="showPromo" :offer="voucher_from_signup_data" />
  <div class="single-page-signup">
    <router-view v-slot="{ Component }">
      <component :is="Component" />
    </router-view>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { useAuthUser } from '../../utils/stores/modules/authUser'
import { useRouter, useRoute } from 'vue-router'
import { ref, computed, onBeforeMount, provide, defineAsyncComponent } from 'vue'
import { useSignupData } from './composables/signup_data.js'
import * as GTM from '../../utils/analytics/gtm'

const SignupPromo = defineAsyncComponent(() => import('../SignupA/elements/SignupPromo.vue'))

const store = useStore()
const userStore = useAuthUser()
const router = useRouter()
const route = useRoute()

const {
  signup_data: user_data,
  updateSignupData: updateUserData,
  signup_data_voucher,
  clearVoucher,
  updateVoucherData,
  signup_data_checkout,
  updateCheckoutData,
  updateDeliveryDate,
  voucher_items,
  delivery_type,
  ignore_card,
  setPlanCache,
} = useSignupData()

const current_version = ref('D')
const hide_500g_plans = ref(true)
const isLoading = ref(false)

const showPromo = computed(() =>
  route.name &&
  route.name !== 'Loader' &&
  !route.name.startsWith('Confirmation')
)

const planSummaryData = computed(() => {
  var has_voucher =
    signup_data_voucher.value.voucher_code !== undefined &&
    signup_data_voucher.value.voucher_code !== ''

  return {
    coffee: user_data.value.coffee,
    bean_type: user_data.value.bean_type,
    coffee_type: user_data.value.coffee_type,
    grind_size: user_data.value.grind_size ? user_data.value.grind_size.replace('_', ' ') : user_data.value.grind_size,
    brewing_method: user_data.value.brewing_method,
    frequency: user_data.value.frequency,
    total: has_voucher ? signup_data_voucher.value.total : user_data.value.total,
    subtotal: has_voucher ? signup_data_voucher.value.subtotal : user_data.value.subtotal,
    discount: signup_data_voucher.value.discount,
    voucher_discount: signup_data_voucher.value.voucher_discount,
    balance_discount: signup_data_voucher.value.balance_discount,
    plan_price: user_data.value.plan?.price,
    plan_pioneer_price: user_data.value.plan?.pioneer_price,
    voucher: signup_data_voucher.value.voucher,
    voucher_code: signup_data_voucher.value.voucher_code,
    plan: user_data.value.plan?.name,
    decaf: user_data.value.decaf,
    amount: user_data.value.amount,
    employee_discount: signup_data_checkout.value.employee_discount,
    is_pioneer: user_data.value.is_pioneer
  }
})

const voucher_from_signup_data = computed(() => {
  return {
    ...signup_data_voucher.value?.voucher,
    frequency: planSummaryData.value?.frequency,
    plan: planSummaryData.value?.plan,
    bag_amount: planSummaryData.value?.amount,
  }
})

onBeforeMount(() => {
  GTM.funnelProgress(1, 'enter_funnel', {})
  GTM.funnelProgress(2, 'start_funnel', {})
  isLoading.value = true
  store.dispatch('shop/getFunnelCoffeesRange', {
    coffee_type: 'espresso',
    decaf: false,
    voucher_code: signup_data_voucher.value.voucher_code
  }).finally(() => {
    setTimeout(() => isLoading.value = false, 1000)
  })

  if (userStore.userType === 'B2bUser' || userStore.userType === 'D2bUser') {
    router.push({ name: 'B2B' })
  }

  var urlParams = new URLSearchParams(window.location.search)
  // if (urlParams.has('showv')) {}
})

provide('setPlanCache', setPlanCache)
provide('increaseStep', () => {})
provide('userData', user_data)
provide('updateUserData', updateUserData)
provide('voucherData', signup_data_voucher)
provide('clearVoucher', clearVoucher)
provide('updateVoucherData', updateVoucherData)
provide('checkoutData', signup_data_checkout)
provide('updateDeliveryDate', updateDeliveryDate)
provide('updateCheckoutData', updateCheckoutData)

provide('voucherItems', voucher_items)
provide('deliveryType', delivery_type)
provide('ignoreCard', ignore_card)

provide('planSummaryData', planSummaryData)
provide('voucher_from_signup_data', voucher_from_signup_data)
provide('show_promo', showPromo)
provide('funnel_version', current_version)
provide('hide_500g_plans', hide_500g_plans)
provide('isLoading', isLoading)
</script>

<script>
export default {
  name: 'SignupRoot',
}
</script>

<style lang="scss">
@use '../../../assets/sass/inc/default/grid/grid-vue-mixins.scss' as *;

.single-page-signup {
  .single-page-signup-title {
    color: #212427;
    font-family: 'Apercu';
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    margin-bottom: 20px;
  }

  .single-page-signup-subtitle {
    color: #7A7C7D;
    font-family: 'Apercu';
    font-size: 16px;
    font-weight: 300;
    line-height: 20px;
    margin-bottom: 0;
    margin-top: 5px;
  }

  .single-page-signup-option {
    padding: 17px; //take 4 px from border
    border: 3px solid #f5f5f5;
    border-radius: 10px;

    &.picked {
      border: 3px solid #30BEA6;
      background: #EAF9F6;
    }

    .single-page-signup-option-text {
      color: #212427;
      font-family: 'Apercu-medium';
      font-style: normal;

      font-weight: 300;
      font-size: 18px;
      line-height: 26px;
      letter-spacing: 0;

      &.price{
        font-family: 'Apercu';
        font-weight: 700;
      }

      &.discounted {
        font-family: 'Apercu';
        font-weight: 700;
        color: #D94822;
      }

      &.old-price{
        font-size: 14px;
        font-family: 'Apercu';
        font-weight: 300 !important;
        line-height: 14px;
        letter-spacing: 0px;
      }
    }

    .single-page-signup-option-arrow{
      height: 16px;
      width: 16px;
    }

    &.three-options{
      &:first-child{
        padding-left: 0;
        padding-right: 10px;
      }

      &:nth-child(2){
        padding-left: 5px;
        padding-right: 5px;
        margin-left: auto;
        margin-right: auto;
      }

      &:last-child{
        padding-left: 10px;
        padding-right: 0;
      }

			@media (min-width: 0px) and (max-width: 360px){
        &:first-child{
          padding-left: 0;
          padding-right: 5px;
        }

        &:nth-child(2){
          padding-left: 0px;
          padding-right: 0px;
          margin-left: auto;
          margin-right: auto;
        }

        &:last-child{
          padding-left: 5px;
          padding-right: 0;
        }
      }

			@media (min-width: 992px) and (max-width: 1199px){
        &:first-child{
          padding-left: 0;
          padding-right: 5px;
        }

        &:nth-child(2){
          padding-left: 0px;
          padding-right: 0px;
          margin-left: auto;
          margin-right: auto;
        }

        &:last-child{
          padding-left: 5px;
          padding-right: 0;
        }
      }

			@media (min-width: 1200px) and (max-width: 1499px){
        &:first-child{
          padding-left: 0;
          padding-right: 6px;
        }

        &:nth-child(2){
          padding-left: 3px;
          padding-right: 3px;
          margin-left: auto;
          margin-right: auto;
        }

        &:last-child{
          padding-left: 6px;
          padding-right: 0;
        }
      }
    }

    .option-roundel {
      position: absolute;
      width: fit-content;
      height: fit-content;
      padding: 5px;
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1;
      border-radius: 25px;
      display: flex;
      justify-content: center;
      align-items: center;

      span {
        color: #fff;
        text-align: center;
        font-family: 'Apercu-medium';
        font-size: 14px;
        font-weight: 700;
        line-height: 14px;
        letter-spacing: 0px;
      }

      &.best-value{
        background-color: #D6F2ED;
        span{
          color: #228574;
        }
      }

      &.most-popular{
        background-color: #FFAA2B;
        span{
          color: #1D4045;
        }
      }
    }
  }

  .single-page-signup-toggle {
    display: inline-flex;
    border-radius: 50px;
    background: #F5F5F5;
    padding: 4px;
    margin-top: 10px;

    @media (min-width: 768px) {
      margin-top: 0;
    }

    .single-page-signup-toggle-option {
      color: #1D4045;
      font-family: 'Apercu';
      font-size: 14px;
      font-weight: 700;
      line-height: 20px;
      padding: 8px 16px;
      border-radius: 50px;
      cursor: pointer;

      &.active {
        background: #fff;
      }
    }
  }
}
</style>
<style lang="scss">
.single-page-signup-modal-btn{
  width: 40px;
  height: 40px;

  &.fillable {
    filter: invert(100%) sepia(100%) saturate(2%) hue-rotate(280deg) brightness(112%) contrast(101%);
  }

  &-close {
    filter: invert(100%) sepia(100%) saturate(2%) hue-rotate(280deg) brightness(112%) contrast(101%);

    @media (min-width: 768px) {
      width: 20px;
      height: 20px;
    }

    &.header-icons {
      position: absolute;
      top: 30px;
      z-index: 10;

      @media (min-width: 768px) {
        top: 40px;
      }

      &.right {
        right: 30px;

        @media (min-width: 768px) {
          right: 40px;
        }
      }
    }
  }

  &.header-icons {
    position: absolute;
    top: 30px;

    &.left {
      left: 30px;
    }

    @media (max-width: 768px) {
      top: 40px;
    }
  }
}

.single-page-signup-modal {
  margin-top: 80px;
  max-width: 482px;

	.single-page-signup-modal-option {
		border-radius: 10px;
		background: #F5F5F5;
		height: 101px;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 12px;

		@media (min-width: 992px) {
			height: 134px;
		}

		&.picked {
			border: 4px solid #30BEA6;
			background: #EAF9F6;
		}

		.prep-img {

			&.espresso,
			&.cafetiere,
			&.chemex,
			&.stovetop,
			&.aeropress {
				min-height: 48px;
				max-height: 48px;

				@media (min-width: 992px) {
					min-height: 65px;
					max-height: 65px;
				}
			}

      &.clever-dripper{
        min-height: 44px;
				max-height: 44px;
      }

			&.v60 {
				min-height: 30px;
				max-height: 30px;

				@media (min-width: 992px) {
					max-height: 41px;
				}
			}
		}
	}

  .single-page-signup-modal-text {
      color: #fff;
      font-family: 'Apercu';
      font-size: 16px;
      font-style: normal;
      font-weight: 300;
      line-height: 24px;
      letter-spacing: 0;
    }
	}
</style>
